import { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { formatDate } from '../../../utils/dateUtils';
import { Snackbar, Alert, Tooltip, IconButton, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, Typography, FormControl, Select, MenuItem, TextField } from '@mui/material';
import { Refresh, GetApp, Search as SearchIcon } from '@mui/icons-material';
import { exportStyledExcel } from '../../../utils/excelExport';
import SaleModal from '../Sale/SaleModal';
import PurchaseModal from '../Purchase/PurchaseModal';
import PaymentReceiptsModal from '../PaymentReceipts/PaymentReceiptsModal';
import JournalEntryModal from '../JournalEntry/JournalEntryModal';

/* ═══════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════ */
const T = {
  bg: '#f8fafc',
  card: '#ffffff',
  cardSolid: '#ffffff',
  border: '#e2e8f0',
  text: '#1e293b',
  textSec: '#475569',
  muted: '#94a3b8',
  purple: '#8b5cf6',
  blue: '#1e40af',
  cyan: '#06b6d4',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#d97706',
  pink: '#ec4899',
  indigo: '#6366f1',
  radius: 8,
  shadow: '0 1px 3px rgba(0,0,0,0.08)',
  shadowLg: '0 2px 8px rgba(0,0,0,0.1)',
};

/* ═══════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════ */
const fmt = n => `₹${(parseFloat(n)||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtN = n => (parseFloat(n)||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtShort = n => {
  const v = parseFloat(n)||0;
  if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v/1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const fmt2Date = d => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};

const TABS = ['Overview','Commission','Pending','Exceeded','Sale Orders','Collections','Stock','Activity Log','Customer Ledger'];

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════ */

/* Clean Card */
const GCard = ({ children, style, accent }) => (
  <div style={{
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: T.radius,
    padding: '14px 16px',
    boxShadow: T.shadow,
    position: 'relative',
    overflow: 'hidden',
    ...style
  }}>
    {accent && <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent, borderRadius:'8px 8px 0 0' }}/>}
    {children}
  </div>
);

/* KPI Metric Card */
const MetricCard = ({ label, value, sub, color, icon }) => (
  <div style={{
    flex:'1 1 140px', minWidth:140, position:'relative', overflow:'hidden',
    background: T.cardSolid, borderRadius: T.radius, padding:'12px 14px',
    border:`1px solid ${T.border}`, boxShadow: T.shadow,
    borderTop: `3px solid ${color}`
  }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
      <div>
        <div style={{ fontSize:10, color:T.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>{label}</div>
        <div style={{ fontSize:20, fontWeight:800, color:T.text, lineHeight:1, letterSpacing:'-0.5px' }}>{value}</div>
        {sub && <div style={{ fontSize:10, color, marginTop:5, fontWeight:600 }}>{sub}</div>}
      </div>
      {icon && <div style={{ width:34, height:34, borderRadius:8, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>{icon}</div>}
    </div>
  </div>
);

/* Section Header */
const SectionTitle = ({ children, right }) => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
    <span style={{ fontSize:13, fontWeight:700, color:T.text, letterSpacing:'-0.2px' }}>{children}</span>
    {right && <span style={{ fontSize:11, color:T.muted, fontWeight:500 }}>{right}</span>}
  </div>
);

/* Chart Tooltip - kept for future use */
// eslint-disable-next-line no-unused-vars
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:'rgba(15,23,42,0.93)', backdropFilter:'blur(12px)',
      border:'1px solid rgba(255,255,255,0.08)', borderRadius:12,
      padding:'10px 14px', boxShadow:'0 12px 40px rgba(0,0,0,0.3)'
    }}>
      <div style={{ color:'rgba(255,255,255,0.5)', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:4 }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ color:'#fff', fontWeight:700, fontSize:13, marginTop:2 }}>
          <span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:p.color||p.fill||'#fff', marginRight:6 }}/>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

/* Badge */
const Badge = ({ label, color, bg }) => (
  <span style={{ padding:'3px 8px', borderRadius:20, fontSize:10, fontWeight:700, background:bg||`${color}15`, color, whiteSpace:'nowrap' }}>{label}</span>
);

/* Data Table */
const DTable = ({ cols, rows, emptyMsg, maxH }) => (
  <div style={{ overflowX:'auto', maxHeight: maxH||380, overflowY:'auto', borderRadius:10 }}>
    <table style={{ borderCollapse:'collapse', width:'100%', fontSize:11 }}>
      <thead>
        <tr>{cols.map((c,i) => (
          <th key={i} style={{ padding:'8px 10px', background:'rgba(99,102,241,0.06)', color:T.blue, fontWeight:700, textAlign:'left', whiteSpace:'nowrap', borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:1, fontSize:10, textTransform:'uppercase', letterSpacing:'0.3px' }}>{c}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={cols.length} style={{ padding:24, textAlign:'center', color:T.muted, fontSize:12 }}>{emptyMsg||'No data available'}</td></tr>
          : rows.map((r,i) => (
            <tr key={i} style={{ background: i%2===0?'transparent':'rgba(0,0,0,0.015)', borderBottom:`1px solid ${T.border}` }}>
              {r.map((cell,j) => (<td key={j} style={{ padding:'7px 10px', color:T.text, whiteSpace:'nowrap' }}>{cell}</td>))}
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

/* Pivot Table */
const PivotSummary = ({ categories, customers, pivot, accentColor, totalLabel }) => {
  const thS = { padding:'6px 10px', fontWeight:700, fontSize:9, whiteSpace:'nowrap', borderBottom:`1px solid ${T.border}`, color:T.blue, background:'rgba(59,130,246,0.05)', position:'sticky', top:0, zIndex:1, textTransform:'uppercase', letterSpacing:'0.3px' };
  const tdS = { padding:'6px 10px', fontSize:11, whiteSpace:'nowrap', borderBottom:`1px solid ${T.border}`, color:T.text };
  if (!customers.length) return <div style={{ padding:32, textAlign:'center', color:T.muted, fontSize:12 }}>No data available</div>;
  return (
    <div style={{ overflowX:'auto', maxHeight:440, overflowY:'auto', borderRadius:10 }}>
      <table style={{ borderCollapse:'collapse', width:'100%', fontSize:11 }}>
        <thead>
          <tr>
            <th style={{ ...thS, minWidth:160, position:'sticky', left:0, zIndex:2, background:'rgba(248,250,252,0.98)' }}>Customer</th>
            {categories.map(cat => (<th key={cat} colSpan={3} style={{ ...thS, textAlign:'center', background:'rgba(59,130,246,0.08)' }}>{cat}</th>))}
            <th colSpan={2} style={{ ...thS, textAlign:'center', background:'rgba(16,185,129,0.08)' }}>Total</th>
          </tr>
          <tr>
            <th style={{ ...thS, position:'sticky', left:0, zIndex:2, background:'rgba(248,250,252,0.98)', top:30 }}></th>
            {categories.map(cat => (<>
              <th key={`${cat}-q`} style={{ ...thS, textAlign:'right', top:30 }}>QTY</th>
              <th key={`${cat}-a`} style={{ ...thS, textAlign:'right', top:30 }}>AMT</th>
              <th key={`${cat}-c`} style={{ ...thS, textAlign:'right', color:accentColor, top:30 }}>COMM</th>
            </>))}
            <th style={{ ...thS, textAlign:'right', top:30 }}>Qty</th>
            <th style={{ ...thS, textAlign:'right', color:accentColor, top:30 }}>{totalLabel||'Commission'}</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((cust, i) => {
            let rowQty=0, rowComm=0;
            const cells = categories.map(cat => { const v=pivot[cust]?.[cat]; rowQty+=v?.qty||0; rowComm+=v?.commission||0; return v; });
            return (
              <tr key={cust} style={{ background: i%2===0?'transparent':'rgba(0,0,0,0.015)' }}>
                <td style={{ ...tdS, fontWeight:600, color:T.purple, position:'sticky', left:0, background:i%2===0?'rgba(248,250,252,0.98)':'rgba(243,244,246,0.98)', zIndex:1 }}>{cust}</td>
                {cells.map((v,ci) => (<>
                  <td key={`q${ci}`} style={{ ...tdS, textAlign:'right', color:T.muted }}>{v?.qtyModeQty ? fmtN(v.qtyModeQty) : ''}</td>
                  <td key={`a${ci}`} style={{ ...tdS, textAlign:'right', color:T.muted }}>{v?.amtModeAmt ? fmtN(v.amtModeAmt) : ''}</td>
                  <td key={`c${ci}`} style={{ ...tdS, textAlign:'right', color:v?.commission?accentColor:T.muted, fontWeight:v?.commission?700:400 }}>{v?.commission ? fmtN(v.commission) : ''}</td>
                </>))}
                <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{rowQty ? fmtN(rowQty) : ''}</td>
                <td style={{ ...tdS, textAlign:'right', fontWeight:700, color:accentColor }}>{rowComm ? fmtN(rowComm) : ''}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop:`2px solid ${T.border}` }}>
            <td style={{ ...tdS, fontWeight:800, color:T.text, position:'sticky', left:0, background:'rgba(248,250,252,0.98)', zIndex:1 }}>Totals</td>
            {categories.map(cat => {
              const tq=customers.reduce((s,c)=>s+(pivot[c]?.[cat]?.qty||0),0);
              const ta=customers.reduce((s,c)=>s+(pivot[c]?.[cat]?.amtModeAmt||0),0);
              const tc=customers.reduce((s,c)=>s+(pivot[c]?.[cat]?.commission||0),0);
              return (<>
                <td key={`tq-${cat}`} style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{tq?fmtN(tq):''}</td>
                <td key={`ta-${cat}`} style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{ta?fmtN(ta):''}</td>
                <td key={`tc-${cat}`} style={{ ...tdS, textAlign:'right', fontWeight:700, color:accentColor }}>{tc?fmtN(tc):''}</td>
              </>);
            })}
            <td style={{ ...tdS, textAlign:'right', fontWeight:800 }}>{fmtN(customers.reduce((s,c)=>s+categories.reduce((ss,cat)=>ss+(pivot[c]?.[cat]?.qty||0),0),0))}</td>
            <td style={{ ...tdS, textAlign:'right', fontWeight:800, color:accentColor }}>{fmtN(customers.reduce((s,c)=>s+categories.reduce((ss,cat)=>ss+(pivot[c]?.[cat]?.commission||0),0),0))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};


/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const SalesManDashboard = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, userId, roleName, username, companyName } = useAuth();
  const { logAudit } = useAuditLog();

  /* ── ROLE DETECTION ── */
  const isExecutiveRole = useMemo(() => {
    const r = (roleName||'').toLowerCase();
    return r.includes('salesman') || r.includes('sales executive') || r.includes('salesexecutive') || r.includes('sales man') || r.includes('executive');
  }, [roleName]);

  const isAdmin = !isExecutiveRole;

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate,   setToDate]   = useState(fmt2Date(today));
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const [executives,     setExecutives]     = useState([]);
  const [salesManId,     setSalesManId]     = useState('');
  const [allCategories,  setAllCategories]  = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [pendingData,    setPendingData]    = useState([]);
  const [exceededData,   setExceededData]   = useState([]);
  const [saleOrders,     setSaleOrders]     = useState([]);
  const [payments,       setPayments]       = useState([]);
  const [stockData,      setStockData]      = useState([]);
  const [salesmanLogs,   setSalesmanLogs]   = useState([]);

  /* ── Ledger tab state ── */
  const [ledgerCustomers,    setLedgerCustomers]    = useState([]);
  const [ledgerCustLoading,  setLedgerCustLoading]  = useState(false);
  const [selectedLedgerCust, setSelectedLedgerCust] = useState('');
  const [custLedger,         setCustLedger]         = useState([]);
  const [custLedgerLoading,  setCustLedgerLoading]  = useState(false);

  /* ── Ledger modal state ── */
  const [modalLoading,          setModalLoading]          = useState(false);
  const [openSaleModal,         setOpenSaleModal]         = useState(false);
  const [openPurchaseModal,     setOpenPurchaseModal]     = useState(false);
  const [openPaymentModal,      setOpenPaymentModal]      = useState(false);
  const [openJournalModal,      setOpenJournalModal]      = useState(false);
  const [saleFormData,          setSaleFormData]          = useState({});
  const [saleDetails,           setSaleDetails]           = useState([]);
  const [ledgerModalCustomers,  setLedgerModalCustomers]  = useState([]);
  const [ledgerItems,           setLedgerItems]           = useState([]);
  const [ledgerBanks,           setLedgerBanks]           = useState([]);
  const [purchaseFormData,      setPurchaseFormData]      = useState({});
  const [purchaseDetails,       setPurchaseDetails]       = useState([]);
  const [suppliers,             setSuppliers]             = useState([]);
  const [paymentFormData,       setPaymentFormData]       = useState({});
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);
  const [paymentMasters,        setPaymentMasters]        = useState([]);
  const [paymentMethods,        setPaymentMethods]        = useState([]);
  const [journalFormData,       setJournalFormData]       = useState({});
  const [journalEntryDetails,   setJournalEntryDetails]   = useState([]);
  const [journalMasters,        setJournalMasters]        = useState([]);

  /* ═══════════════════════════════════════
     DATA LOADING — ROLE-BASED LOGIC
     - Executive role: userId → match from list → auto-set salesManId
     - Admin role: show dropdown, user selects value
  ═══════════════════════════════════════ */
  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('SalesExecutiveApi', `/${companyId}`, token)
      .then(r => {
        const list = r?.response_data || r || [];
        setExecutives(list);
        if (isExecutiveRole && userId) {
          const uname = (username || '').toLowerCase().trim();
          const uid = String(userId);
          // Try multiple matching strategies to find the executive
          const matched =
            // 1. Match by UserId field (if available)
            list.find(e => String(e.UserId || '') === uid) ||
            // 2. Match by username against Name or SearchableName
            (uname ? list.find(e => (e.Name||'').toLowerCase().trim() === uname ||
                                    (e.SearchableName||'').toLowerCase().trim() === uname) : null) ||
            // 3. Match by userId against executive Id
            list.find(e => String(e.Id) === uid) ||
            // 4. Partial match — username contained in Name or vice versa
            (uname ? list.find(e => (e.Name||'').toLowerCase().trim().includes(uname) ||
                                    uname.includes((e.Name||'').toLowerCase().trim())) : null) ||
            null;

          console.log('[SalesManDashboard] Executive matching:', { userId, username, matchedExec: matched, listCount: list.length, listSample: list.slice(0,3) });
          if (matched) {
            setSalesManId(String(matched.Id));
          } else if (list.length === 1) {
            // Only one executive in the list, use that one
            setSalesManId(String(list[0].Id));
          }
          // If no match found, executive can select from dropdown
        }
      }).catch(() => {});
    fetchData('CategoryApi', `/${companyId}`, token)
      .then(r => setAllCategories(r?.response_data || r || []))
      .catch(() => {});
  }, [companyId, token, userId, username, isExecutiveRole]);

  /* ── Fetch all dashboard data ── */
  const fetchAll = useCallback(async () => {
    setLoading(true); setError('');
    const sm = salesManId || '0';
    // Always use salesManId (executive's Id matched from list, or admin dropdown selection)
    // salesManId is correctly set for both roles in the useEffect above
    const commSm = sm;
    try {
      const results = await Promise.allSettled([
        fetchData('SaleReportApi', `/SalesmanCommissionDetails/${companyId}/${branchId}/${commSm}/${fromDate}/${toDate}`, token),
        fetchData('SaleReportApi', `/SalesmanCommissionPendingDetails/${companyId}/${branchId}/${commSm}/${fromDate}/${toDate}`, token),
        fetchData('SaleReportApi', `/SalesmanCommissionExceededDetails/${companyId}/${branchId}/${commSm}/${fromDate}/${toDate}`, token),
        fetchData('SaleOrderApi',  `/${companyId}/${branchId}/${fromDate}/${toDate}`, token),
        fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token),
        fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token),
        Promise.resolve([]), // SalesManLog API not available
      ]);
      const g = r => {
        if (r.status === 'rejected') {
          console.error('API failed:', r.reason);
          return [];
        }
        return r.value?.response_data || r.value || [];
      };
      setCommissionData(g(results[0]));
      setPendingData(g(results[1]));
      setExceededData(g(results[2]));
      setSaleOrders(g(results[3]));
      setPayments(g(results[4]));
      setStockData(g(results[5]));
      setSalesmanLogs(g(results[6]));
    } catch (err) {
      console.error('fetchAll error:', err);
      setError('Failed to load dashboard data.');
    }
    setLoading(false);
  }, [companyId, branchId, fromDate, toDate, salesManId, token]);

  // Auto-fetch: triggers when salesManId is set, or immediately for Admin (uses '0' = all)
  useEffect(() => {
    if (!companyId || !token) return;
    // For executive: wait until they're matched or selected from dropdown
    // For admin: fetch immediately (salesManId '' means "All Salesmen" = pass 0)
    if (isExecutiveRole && !salesManId) return;
    const doFetch = async () => {
      setLoading(true); setError('');
      const sm = salesManId || '0';
      console.log('[SalesManDashboard] Fetching data with:', { companyId, branchId, salesManId: sm, fromDate, toDate, isExecutiveRole });
      try {
        const results = await Promise.allSettled([
          fetchData('SaleReportApi', `/SalesmanCommissionDetails/${companyId}/${branchId}/${sm}/${fromDate}/${toDate}`, token),
          fetchData('SaleReportApi', `/SalesmanCommissionPendingDetails/${companyId}/${branchId}/${sm}/${fromDate}/${toDate}`, token),
          fetchData('SaleReportApi', `/SalesmanCommissionExceededDetails/${companyId}/${branchId}/${sm}/${fromDate}/${toDate}`, token),
          fetchData('SaleOrderApi',  `/${companyId}/${branchId}/${fromDate}/${toDate}`, token),
          fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token),
          fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token),
          Promise.resolve([]),
        ]);
        const g = r => {
          if (r.status === 'rejected') { console.error('API failed:', r.reason); return []; }
          return r.value?.response_data || r.value || [];
        };
        console.log('[SalesManDashboard] API results:', { 
          commission: g(results[0]).length, 
          pending: g(results[1]).length, 
          exceeded: g(results[2]).length,
          saleOrders: g(results[3]).length,
          payments: g(results[4]).length,
          stock: g(results[5]).length
        });
        setCommissionData(g(results[0]));
        setPendingData(g(results[1]));
        setExceededData(g(results[2]));
        setSaleOrders(g(results[3]));
        setPayments(g(results[4]));
        setStockData(g(results[5]));
        setSalesmanLogs(g(results[6]));
      } catch (err) {
        console.error('fetchAll error:', err);
        setError('Failed to load dashboard data.');
      }
      setLoading(false);
    };
    doFetch();
  }, [companyId, branchId, token, salesManId, isExecutiveRole, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Ledger tab: fetch customers ── */
  useEffect(() => {
    if (activeTab !== 8 || !companyId || !branchId || !token) return;
    const execId = salesManId || '0';
    setLedgerCustLoading(true);
    setLedgerCustomers([]); setSelectedLedgerCust(''); setCustLedger([]);
    fetchData('SalesExecutiveApi', `/ExecutiveWiseCustomer/${companyId}/${branchId}/${execId}`, token)
      .then(r => setLedgerCustomers(r?.response_data || r || []))
      .catch(() => setLedgerCustomers([]))
      .finally(() => setLedgerCustLoading(false));
  }, [activeTab, companyId, branchId, salesManId, token]);

  /* ── Ledger: fetch when customer selected ── */
  useEffect(() => {
    if (!selectedLedgerCust || !companyId || !token) { setCustLedger([]); return; }
    setCustLedgerLoading(true);
    fetchData('ReportApi', `/LedgerDetails/${companyId}/${selectedLedgerCust}/${fromDate}/${toDate}`, token)
      .then(r => {
        const raw = r?.response_data || r || [];
        setCustLedger(raw.filter((item, idx, arr) => {
          const desc = (item.description1||'').toLowerCase();
          if (desc.includes('opening balance')) return idx === arr.findIndex(e => (e.description1||'').toLowerCase().includes('opening balance'));
          return true;
        }));
      })
      .catch(() => setCustLedger([]))
      .finally(() => setCustLedgerLoading(false));
  }, [selectedLedgerCust, companyId, fromDate, toDate, token]);

  /* ── Ledger modal helpers ── */
  const fetchLedgerModalData = useCallback(async () => {
    if (ledgerModalCustomers.length && ledgerItems.length && ledgerBanks.length) return;
    const [cr, ir, br, sr] = await Promise.allSettled([
      fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token),
      fetchData('ItemApi', `/${companyId}`, token),
      fetchData('BankApi', `/${companyId}`, token),
      fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token),
    ]);
    const g = r => r.status === 'fulfilled' ? (r.value?.response_data || r.value || []) : [];
    setLedgerModalCustomers(g(cr)); setLedgerItems(g(ir)); setLedgerBanks(g(br)); setSuppliers(g(sr));
  }, [companyId, token, ledgerModalCustomers.length, ledgerItems.length, ledgerBanks.length]);

  const handleLedgerDescClick = useCallback(async (row) => {
    const desc = (row.description1 || '').toLowerCase();
    if (desc.includes('opening balance') || desc.includes('total') || desc.includes('closing balance')) return;
    const tbl = (row.tbl1 || '').toLowerCase();
    const id  = row.id;
    if (!tbl || !id || ['az','zy','zz'].includes(tbl)) return;
    await fetchLedgerModalData();
    switch (tbl) {
      case 'sale': {
        setModalLoading(true);
        try { const r = await fetchData('SaleApi', `/${id}`, token); const d = r?.response_data||r; const s = d?.Sale||d; const smName = s?.SalesManName || (executives.find(e => String(e.Id) === String(s?.SalesManId))?.Name) || ''; setSaleFormData({...s, BillDate:s?.BillDate?.split('T')[0]||'', SalesManName: smName}); setSaleDetails(d?.SaleDetails||[]); setOpenSaleModal(true); } catch {} finally { setModalLoading(false); }
        break;
      }
      case 'purchase': {
        setModalLoading(true);
        try { const r = await fetchData('PurchaseApi', `/${id}`, token); const d = r?.response_data||r; const p = d?.Purchase||d; setPurchaseFormData({...p, BillDate:p?.BillDate?.split('T')[0]||''}); setPurchaseDetails(d?.PurchaseDetails||[]); setOpenPurchaseModal(true); } catch {} finally { setModalLoading(false); }
        break;
      }
      case 'paymentandreceipt': case 'payment': case 'receipt': {
        setModalLoading(true);
        try {
          const [mr, mdr] = await Promise.allSettled([
            paymentMasters.length ? Promise.resolve({response_data:paymentMasters}) : fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token),
            paymentMethods.length ? Promise.resolve({response_data:paymentMethods}) : fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token),
          ]);
          const lm = mr.status==='fulfilled' ? (mr.value?.response_data||mr.value||[]) : [];
          const lmd = mdr.status==='fulfilled' ? (mdr.value?.response_data||mdr.value||[]) : [];
          if (!paymentMasters.length) setPaymentMasters(lm);
          if (!paymentMethods.length) setPaymentMethods(lmd);
          const r = await fetchData('PaymentAndReceiptApi', `/PaymentData/${id}`, token);
          const d = r?.response_data||r; const p = d?.PaymentAndReceipt||d;
          setPaymentFormData({...p, BillDate:p?.BillDate?.split('T')[0]||'', ChequeDate:p?.ChequeDate?.split('T')[0]||''});
          const pDet = d?.PaymentAndReceiptPurchase||[]; const sDet = d?.PaymentAndReceiptSale||[];
          setPaymentReceiptDetails(
            p?.IsType && pDet.length ? pDet.map(x => ({Id:x.PurchaseId, Description:x.PurchaseDescription||'', Amount:x.PurchaseAmount||0, PaidAmount:x.Amount||0, BalanceAmount:0, PendingAmount:0, BillDate:x.BillDate||p.BillDate, Type:'Purchase', PaymentAmount:x.Amount||0, IsSelected:true}))
            : !p?.IsType && sDet.length ? sDet.map(x => ({Id:x.SaleId, Description:x.SaleDescription||'', Amount:x.SaleAmount||0, PaidAmount:x.Amount||0, BalanceAmount:0, PendingAmount:0, BillDate:x.BillDate||p.BillDate, Type:'Sale', PaymentAmount:x.Amount||0, IsSelected:true}))
            : [{noData:true}]
          );
          setOpenPaymentModal(true);
        } catch {} finally { setModalLoading(false); }
        break;
      }
      case 'journalentry': case 'journal': {
        setModalLoading(true);
        try {
          if (!journalMasters.length) { const jm = await fetchData('JournalEntryApi', `/Master/${companyId}`, token); setJournalMasters(jm?.response_data||jm||[]); }
          const r = await fetchData('JournalEntryApi', `/JournalEntryData/${id}`, token);
          const jd = r?.response_data||r; const je = jd?.JournalEntry;
          if (je) { setJournalFormData({...je, BillDate:je.BillDate?.split('T')[0]||'', IsType:je.IsType===true?'Credit':'Debit'}); setJournalEntryDetails((jd?.JournalEntryDetails||[]).filter(d => d.MasterId !== je.MasterId)); }
          setOpenJournalModal(true);
        } catch {} finally { setModalLoading(false); }
        break;
      }
      default:
        if (tbl.startsWith('payment') || tbl.startsWith('receipt')) handleLedgerDescClick({...row, tbl1:'payment'});
        break;
    }
  }, [companyId, token, fetchLedgerModalData, paymentMasters, paymentMethods, journalMasters]);

  /* ── Ledger export ── */
  const handleLedgerExport = useCallback(async () => {
    if (!custLedger.length) return;
    const custName = (ledgerCustomers.find(c => String(c.MasterId||c.CustomerId||c.Id||c.CustId||c.AccountId) === String(selectedLedgerCust))?.Name) || 'Customer';
    let runBal = 0;
    const rows = custLedger.map((d, i) => {
      const debit=parseFloat(d.debit)||0; const credit=parseFloat(d.credit)||0;
      const isTotal=(d.description1||'').toLowerCase().includes('total');
      if (!isTotal) runBal += credit - debit;
      return [i+1, d.date1 ? new Date(d.date1).toLocaleDateString('en-IN') : '-', d.description1||'-', debit>0?debit:'', credit>0?credit:'', `${Math.abs(runBal).toFixed(2)} ${runBal<0?'Dr':'Cr'}`];
    });
    await exportStyledExcel({
      reportTitle: 'CUSTOMER LEDGER',
      sheetName: 'Ledger',
      fileName: `Ledger_${custName}_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: ['#','Date','Description','Debit','Credit','Balance'],
      rows,
    });
  }, [custLedger, ledgerCustomers, selectedLedgerCust, fromDate, toDate, companyName, username]);

  /* ═══════════════════════════════════════
     COMPUTED DATA
  ═══════════════════════════════════════ */
  const totalComm    = useMemo(() => commissionData.reduce((s,d) => s+(parseFloat(d.CommissionAmount||d.Commission)||0), 0), [commissionData]);
  const totalPending = useMemo(() => pendingData.reduce((s,d) => s+(parseFloat(d.CommissionAmount||d.Commission)||0), 0), [pendingData]);
  const totalExc     = useMemo(() => exceededData.reduce((s,d) => s+(parseFloat(d.CommissionAmount||d.Commission)||0), 0), [exceededData]);
  const totalSO      = useMemo(() => saleOrders.reduce((s,d) => s+(parseFloat(d.TotalAmount)||0), 0), [saleOrders]);
  const totalPay     = useMemo(() => payments.reduce((s,d) => s+(parseFloat(d.TotalAmount||d.Amount)||0), 0), [payments]);



  const buildPivot = useCallback((data) => {
    if (!data.length) return { categories: allCategories.map(c=>c.Name), customers:[], pivot:{} };
    const custMap={}, piv={};
    data.forEach(r => {
      const cust=r.CustomerName||'(Unknown)'; const cat=r.CategoryName||'(Unknown)';
      if (!custMap[cust]) custMap[cust]=cust;
      if (!piv[cust]) piv[cust]={};
      if (!piv[cust][cat]) piv[cust][cat]={qty:0,commission:0,qtyModeQty:0,amtModeAmt:0};
      piv[cust][cat].qty += parseFloat(r.Quantity)||0;
      piv[cust][cat].commission += parseFloat(r.CommissionAmount)||0;
      if ((r.CommissionMode||'').toUpperCase()==='QTY') piv[cust][cat].qtyModeQty += parseFloat(r.Quantity)||0;
      else piv[cust][cat].amtModeAmt += parseFloat(r.TaxableAmount)||0;
    });
    return { categories:allCategories.map(c=>c.Name), customers:Object.keys(custMap), pivot:piv };
  }, [allCategories]);
  const commPivot = useMemo(() => buildPivot(commissionData), [commissionData, buildPivot]);
  const pendPivot = useMemo(() => buildPivot(pendingData),    [pendingData, buildPivot]);
  const excPivot  = useMemo(() => buildPivot(exceededData),   [exceededData, buildPivot]);

  const selectedExecName = executives.find(e => String(e.Id) === salesManId)?.Name || '';

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SalesManCommission', 0);
    const headers = ['#', 'Salesman', 'Customer', 'Bill No', 'Date', 'Sale Amt', 'Commission'];
    const rows = commissionData.map((d,i) => [i+1, d.SalesManName||'-', d.CustomerName||'-', d.BillNumber||'-', formatDate(d.BillDate), fmtN(d.SaleAmount||d.Amount), fmtN(d.CommissionAmount||d.Commission)]);

      await exportStyledExcel({
          reportTitle: 'SALESMAN DASHBOARD',
          sheetName: 'Dashboard',
          fileName: `SalesmanDashboard_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: headers,
          rows,
      });
  };


  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <div style={{ minHeight:'100vh', background:T.bg, padding:'12px 14px', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", color:T.text, boxSizing:'border-box' }}>
      <LogoLoader open={loading} message="Loading..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius:3 }}>{error}</Alert>
      </Snackbar>

      {/* ═══ HEADER ═══ */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, flexWrap:'wrap' }}>
        <button onClick={() => history.goBack()} style={{
          width:32, height:32, borderRadius:6, border:`1px solid ${T.border}`,
          background:T.card, color:T.text, cursor:'pointer', fontSize:14,
          boxShadow:T.shadow, display:'flex', alignItems:'center', justifyContent:'center'
        }}>←</button>

        <div style={{ flex:'0 0 auto' }}>
          <div style={{ fontSize:9, color:T.muted, fontWeight:600, letterSpacing:'1px', textTransform:'uppercase' }}>Salesman Dashboard</div>
          <div style={{ fontSize:16, fontWeight:700, color:T.text, lineHeight:1.3, marginTop:1 }}>
            {isExecutiveRole
              ? <>Welcome, <span style={{ color:T.purple }}>{username || selectedExecName || 'Executive'}</span></>
              : <span style={{ color:T.blue }}>Admin View</span>
            }
          </div>
        </div>

        <div style={{ flex:1 }} />

        {isAdmin ? (
          <select value={salesManId} onChange={e => setSalesManId(e.target.value)} style={{
            height:32, background:T.card, border:`1px solid ${T.border}`,
            borderRadius:6, color:T.text, padding:'0 10px', fontSize:12, cursor:'pointer', outline:'none', minWidth:150
          }}>
            <option value="">All Salesmen</option>
            {executives.map(e => <option key={e.Id} value={String(e.Id)}>{e.Name}</option>)}
          </select>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:`${T.purple}12`, border:`1px solid ${T.purple}30`, borderRadius:6 }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:T.green }}/>
            <span style={{ fontSize:12, fontWeight:700, color:T.purple }}>{selectedExecName || username || 'My Dashboard'}</span>
          </div>
        )}

        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{
          height:32, background:T.card, border:`1px solid ${T.border}`,
          borderRadius:6, color:T.text, padding:'0 8px', fontSize:11, outline:'none'
        }}/>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{
          height:32, background:T.card, border:`1px solid ${T.border}`,
          borderRadius:6, color:T.text, padding:'0 8px', fontSize:11, outline:'none'
        }}/>
        <button onClick={fetchAll} style={{
          height:32, padding:'0 14px', borderRadius:6, border:'none',
          background:T.blue, color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer'
        }}>Apply</button>
        <button onClick={handleExport} style={{
          height:32, padding:'0 12px', borderRadius:6,
          border:`1px solid ${T.border}`, background:T.card, color:T.text,
          fontSize:12, fontWeight:500, cursor:'pointer'
        }}>⬇ Export</button>
      </div>

      {/* ═══ KPI METRICS ═══ */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <MetricCard label="Commission Earned" value={fmtShort(totalComm)} sub={`${commissionData.length} bills`} color={T.purple} icon="💰"/>
        <MetricCard label="Pending" value={fmtShort(totalPending)} sub={`${pendingData.length} bills`} color={T.amber} icon="⏳"/>
        <MetricCard label="Exceeded" value={fmtShort(totalExc)} sub={`${exceededData.length} bills`} color={T.red} icon="⚠️"/>
        <MetricCard label="Sale Orders" value={saleOrders.length} sub={fmtShort(totalSO)} color={T.blue} icon="🛒"/>
        <MetricCard label="Collections" value={payments.length} sub={fmtShort(totalPay)} color={T.green} icon="💳"/>
        <MetricCard label="Active Salesmen" value={executives.length} sub="registered" color={T.indigo} icon="👥"/>
      </div>

      {/* ═══ TAB BAR ═══ */}
      <div style={{ display:'flex', gap:3, marginBottom:12, overflowX:'auto', scrollbarWidth:'none', background:T.card, padding:4, borderRadius:T.radius, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
        {TABS.map((t,i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{
            padding:'6px 12px', borderRadius:5, border:'none', fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
            background: activeTab===i ? T.blue : 'transparent',
            color: activeTab===i ? '#fff' : T.muted,
            transition:'all 0.15s'
          }}>{t}</button>
        ))}
      </div>

      {/* ═══════════════════════════════════
         TAB 0: OVERVIEW
      ═══════════════════════════════════ */}
      {activeTab === 0 && (
        <GCard>
          <SectionTitle>Summary</SectionTitle>
          <DTable
            cols={['Metric','Count','Amount']}
            rows={[
              ['Commission Earned', commissionData.length, <span style={{ color:T.purple, fontWeight:700 }}>{fmt(totalComm)}</span>],
              ['Pending Commission', pendingData.length, <span style={{ color:T.amber, fontWeight:700 }}>{fmt(totalPending)}</span>],
              ['Exceeded Commission', exceededData.length, <span style={{ color:T.red, fontWeight:700 }}>{fmt(totalExc)}</span>],
              ['Sale Orders', saleOrders.length, <span style={{ color:T.blue, fontWeight:700 }}>{fmt(totalSO)}</span>],
              ['Collections', payments.length, <span style={{ color:T.green, fontWeight:700 }}>{fmt(totalPay)}</span>],
              ['Active Salesmen', executives.length, '—'],
            ]}
            maxH={300}
          />
        </GCard>
      )}


      {/* ═══ TAB 1: COMMISSION ═══ */}
      {activeTab === 1 && (
        <GCard accent={T.green}>
          <SectionTitle right={`${commissionData.length} records | ${fmt(totalComm)}`}>Commission Details</SectionTitle>
          <PivotSummary categories={commPivot.categories} customers={commPivot.customers} pivot={commPivot.pivot} accentColor={T.green} totalLabel="Commission"/>
        </GCard>
      )}

      {/* ═══ TAB 2: PENDING ═══ */}
      {activeTab === 2 && (
        <GCard accent={T.amber}>
          <SectionTitle right={`${pendingData.length} records | ${fmt(totalPending)}`}><span style={{ color:T.amber }}>⏳ Commission Pending</span></SectionTitle>
          <PivotSummary categories={pendPivot.categories} customers={pendPivot.customers} pivot={pendPivot.pivot} accentColor={T.amber} totalLabel="Pending"/>
        </GCard>
      )}

      {/* ═══ TAB 3: EXCEEDED ═══ */}
      {activeTab === 3 && (
        <GCard accent={T.red}>
          <SectionTitle right={`${exceededData.length} records | ${fmt(totalExc)}`}><span style={{ color:T.red }}>⚠️ Commission Exceeded</span></SectionTitle>
          <PivotSummary categories={excPivot.categories} customers={excPivot.customers} pivot={excPivot.pivot} accentColor={T.red} totalLabel="Exceeded"/>
        </GCard>
      )}

      {/* ═══ TAB 4: SALE ORDERS ═══ */}
      {activeTab === 4 && (
        <GCard accent={T.blue}>
          <SectionTitle right={`${saleOrders.length} orders | ${fmt(totalSO)}`}>Sale Orders</SectionTitle>
          <DTable
            cols={['#','SO No','Date','Customer','Salesman','Amount','Status','Location']}
            rows={saleOrders.map((d,i) => [
              i+1,
              <span style={{ color:T.blue, fontWeight:700 }}>{d.SaleOrderNo||'-'}</span>,
              formatDate(d.SaleOrderDate),
              d.CustomerName||d.MasterName||'-',
              d.SalesManName||'-',
              <span style={{ textAlign:'right', display:'block', fontWeight:700 }}>{fmt(d.TotalAmount)}</span>,
              <Badge label={d.IsConverted?'Converted':d.IsVerified?'Verified':'Open'} color={d.IsConverted?T.cyan:d.IsVerified?T.green:T.muted}/>,
              d.Latitude && d.Longitude
                ? <span style={{ color:T.blue, cursor:'pointer' }} onClick={() => window.open(`https://maps.google.com/?q=${d.Latitude},${d.Longitude}`,'_blank')}>📍 View</span>
                : <span style={{ color:T.muted }}>—</span>
            ])}
          />
        </GCard>
      )}

      {/* ═══ TAB 5: COLLECTIONS ═══ */}
      {activeTab === 5 && (
        <GCard accent={T.green}>
          <SectionTitle right={`${payments.length} entries | ${fmt(totalPay)}`}>Payment Collections</SectionTitle>
          <DTable
            cols={['#','Bill No','Date','Customer','Type','Method','Amount']}
            rows={payments.map((d,i) => [
              i+1, d.BillNumber||'-', formatDate(d.BillDate), d.MasterName||'-',
              <Badge label={d.IsType?'Payment':'Receipt'} color={d.IsType?T.red:T.green}/>,
              d.PaymentTypeName||d.PaymentMethod||'-',
              <span style={{ color:T.green, fontWeight:700, textAlign:'right', display:'block' }}>{fmt(d.TotalAmount||d.Amount)}</span>
            ])}
          />
        </GCard>
      )}

      {/* ═══ TAB 6: STOCK ═══ */}
      {activeTab === 6 && (
        <div>
          <GCard>
            <SectionTitle right={`${stockData.length} items`}>Stock Overview</SectionTitle>
            <DTable
              cols={['#','Item','HSN','Stock Qty','Rate','Value']}
              rows={stockData.map((d,i) => {
                const qty = parseFloat(d.StockQuantity||d.Quantity)||0;
                return [
                  i+1,
                  <span style={{ fontWeight:600 }}>{d.ItemName||d.Name||'-'}</span>,
                  d.HSNCode||'-',
                  <span style={{ color:qty<=0?T.red:T.green, fontWeight:700, textAlign:'right', display:'block' }}>{fmtN(qty)}</span>,
                  <span style={{ textAlign:'right', display:'block' }}>{fmt(d.SaleRate||d.Rate)}</span>,
                  <span style={{ color:T.purple, fontWeight:700, textAlign:'right', display:'block' }}>{fmt(qty*(parseFloat(d.SaleRate||d.Rate)||0))}</span>
                ];
              })}
            />
          </GCard>
        </div>
      )}

      {/* ═══ TAB 7: ACTIVITY LOG ═══ */}
      {activeTab === 7 && (
        <GCard accent={T.indigo}>
          <SectionTitle right={`${salesmanLogs.length} entries`}>Salesman Activity Log</SectionTitle>
          <DTable
            cols={['#','Date & Time','Salesman','Entry Type','Reference','Customer','Amount','Location']}
            rows={salesmanLogs.map((d,i) => {
              const etype = d.EntryTypeName||d.EntryType||'-';
              const isSale = etype.toLowerCase().includes('sale');
              const isPay  = etype.toLowerCase().includes('payment');
              return [
                i+1,
                `${formatDate(d.LogDate||d.CreatedDate)} ${d.LogTime||''}`,
                <span style={{ color:T.purple, fontWeight:700 }}>{d.SalesManName||'-'}</span>,
                <Badge label={etype} color={isSale?T.green:isPay?T.blue:T.muted}/>,
                d.ReferenceNo||d.BillNumber||'-',
                d.CustomerName||d.MasterName||'-',
                d.Amount ? <span style={{ color:T.green, fontWeight:700 }}>{fmt(d.Amount)}</span> : '-',
                d.Latitude && d.Longitude
                  ? <span style={{ color:T.blue, cursor:'pointer' }} onClick={() => window.open(`https://maps.google.com/?q=${d.Latitude},${d.Longitude}`,'_blank')}>📍 View</span>
                  : <span style={{ color:T.muted }}>—</span>
              ];
            })}
          />
        </GCard>
      )}


      {/* ═══ TAB 8: CUSTOMER LEDGER ═══ */}
      {activeTab === 8 && (
        <div style={{ borderRadius:T.radius, overflow:'hidden' }}>
          <GCard style={{ padding:0 }}>
            {/* Toolbar */}
            <div style={{ display:'flex', alignItems:'center', padding:'10px 16px', background:T.blue, gap:8, flexWrap:'nowrap' }}>
              <span style={{ fontSize:14, fontWeight:700, color:'#fff', whiteSpace:'nowrap' }}>Customer Ledger</span>
              <div style={{ flex:1 }}/>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={() => selectedLedgerCust && setCustLedger([])} size="small" sx={{ border:'1px solid rgba(255,255,255,0.2)', borderRadius:'8px', height:30, width:30, color:'#fff', '&:hover':{background:'rgba(255,255,255,0.1)'} }}>
                  <Refresh sx={{ fontSize:16 }}/>
                </IconButton>
              </Tooltip>
              <Tooltip title="Export" arrow>
                <IconButton onClick={handleLedgerExport} disabled={!custLedger.length} size="small" sx={{ border:'1px solid rgba(255,255,255,0.2)', borderRadius:'8px', height:30, width:30, color:'#fff', '&:hover':{background:'rgba(255,255,255,0.1)'}, '&.Mui-disabled':{color:'rgba(255,255,255,0.3)'} }}>
                  <GetApp sx={{ fontSize:16 }}/>
                </IconButton>
              </Tooltip>
            </div>

            {/* Filters */}
            <div style={{ padding:'10px 16px', borderBottom:`1px solid ${T.border}`, background:T.cardSolid, overflowX:'auto' }}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-end', minWidth:'fit-content' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:140 }}>
                  <label style={{ fontSize:10, color:T.muted, fontWeight:600 }}>From Date</label>
                  <TextField type="date" size="small" value={fromDate} onChange={e => setFromDate(e.target.value)}
                    sx={{ width:140, '& .MuiOutlinedInput-root':{ borderRadius:'8px', backgroundColor:'#f8fafc', fontSize:'11px', height:'34px', '&:hover fieldset':{borderColor:T.purple}, '&.Mui-focused fieldset':{borderColor:T.purple} } }}/>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:140 }}>
                  <label style={{ fontSize:10, color:T.muted, fontWeight:600 }}>To Date</label>
                  <TextField type="date" size="small" value={toDate} onChange={e => setToDate(e.target.value)}
                    sx={{ width:140, '& .MuiOutlinedInput-root':{ borderRadius:'8px', backgroundColor:'#f8fafc', fontSize:'11px', height:'34px', '&:hover fieldset':{borderColor:T.purple}, '&.Mui-focused fieldset':{borderColor:T.purple} } }}/>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:250 }}>
                  <label style={{ fontSize:10, color:T.muted, fontWeight:600 }}>Customer</label>
                  <FormControl size="small" sx={{ minWidth:250 }}>
                    <Select value={selectedLedgerCust} onChange={e => { setSelectedLedgerCust(e.target.value); setCustLedger([]); }} displayEmpty
                      sx={{ borderRadius:'8px', backgroundColor:'#f8fafc', fontSize:'11px', height:'34px', '&:hover .MuiOutlinedInput-notchedOutline':{borderColor:T.purple}, '&.Mui-focused .MuiOutlinedInput-notchedOutline':{borderColor:T.purple} }}
                      renderValue={v => {
                        if (!v) return <span style={{ color:T.muted, fontSize:'11px' }}>{ledgerCustLoading ? 'Loading...' : 'Select Customer'}</span>;
                        return ledgerCustomers.find(c => String(c.MasterId||c.CustomerId||c.Id||c.CustId||c.AccountId) === String(v))?.Name || 'Select';
                      }}>
                      <MenuItem value="" sx={{ fontSize:'11px', display:'none' }}>Select Customer</MenuItem>
                      {ledgerCustomers.map((c, i) => {
                        const id = c.MasterId||c.CustomerId||c.Id||c.CustId||c.AccountId||i;
                        return <MenuItem key={id} value={String(id)} sx={{ fontSize:'11px' }}>{c.Name}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </div>
                <Button variant="contained" disabled={!selectedLedgerCust||custLedgerLoading}
                  onClick={() => { if (selectedLedgerCust) { setCustLedger([]); setCustLedgerLoading(true); fetchData('ReportApi', `/LedgerDetails/${companyId}/${selectedLedgerCust}/${fromDate}/${toDate}`, token).then(r => { const raw=r?.response_data||r||[]; setCustLedger(raw.filter((item,idx,arr)=>{const d=(item.description1||'').toLowerCase();if(d.includes('opening balance'))return idx===arr.findIndex(e=>(e.description1||'').toLowerCase().includes('opening balance'));return true;}));}).catch(()=>setCustLedger([])).finally(()=>setCustLedgerLoading(false)); } }}
                  sx={{ height:34, px:2, borderRadius:'6px', textTransform:'none', fontSize:12, fontWeight:600, background: selectedLedgerCust&&!custLedgerLoading ? T.blue : '#e5e7eb', color: selectedLedgerCust&&!custLedgerLoading ? '#fff' : T.muted, boxShadow:'none', '&:hover':{boxShadow:'none'}, '&.Mui-disabled':{background:'#e5e7eb',color:T.muted} }}>
                  {custLedgerLoading ? <CircularProgress size={16} sx={{ color:T.muted }}/> : <SearchIcon style={{ fontSize:16, marginRight:4 }}/>}
                  {custLedgerLoading ? 'Loading...' : 'Get Ledger'}
                </Button>
                <Button variant="contained" onClick={handleLedgerExport} disabled={!custLedger.length}
                  sx={{ height:34, px:2, borderRadius:'6px', textTransform:'none', fontSize:12, fontWeight:600, background: custLedger.length ? T.green : '#e5e7eb', color: custLedger.length ? '#fff' : T.muted, boxShadow:'none', '&:hover':{boxShadow:'none'}, '&.Mui-disabled':{background:'#e5e7eb',color:T.muted} }}>
                  <GetApp style={{ fontSize:16, marginRight:4 }}/> Export
                </Button>
              </div>
            </div>

            {/* Ledger Table */}
            <div style={{ overflow:'auto', maxHeight:'calc(100vh - 280px)' }}>
              <TableContainer component={Paper} style={{ boxShadow:'none', borderRadius:0 }}>
                <Table sx={{ minWidth:650 }} size="small">
                  <TableHead>
                    <TableRow style={{ background:T.blue, height:28 }}>
                      {[
                        { label:'Sl No', align:'center', width:'5%' },
                        { label:'Date', align:'left', width:'10%' },
                        { label:'Description', align:'left', width:'35%' },
                        { label:'Debit', align:'right', width:'13%' },
                        { label:'Credit', align:'right', width:'13%' },
                        { label:'Balance', align:'right', width:'14%' },
                      ].map(col => (
                        <TableCell key={col.label} style={{ color:'white', fontWeight:600, fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.3px', padding:'3px 8px', textAlign:col.align, height:28, width:col.width }}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      let runBal = 0;
                      return custLedger.map((ledger, index) => {
                        const debit=parseFloat(ledger.debit)||0; const credit=parseFloat(ledger.credit)||0;
                        const desc=(ledger.description1||'').toLowerCase();
                        const isTotal=desc.includes('total'); const isClose=desc.includes('closing balance');
                        if (!isTotal) runBal += credit - debit;
                        const tbl=(ledger.tbl1||'').toLowerCase();
                        const clickable=tbl&&ledger.id&&!['az','zy','zz'].includes(tbl)&&!desc.includes('opening balance')&&!isTotal&&!isClose;
                        return (
                          <TableRow key={ledger.id||index} sx={{ '&:nth-of-type(even)':{backgroundColor:'#f8fafc'}, '&:hover':{backgroundColor:'#f1f5f9'}, height:30 }}>
                            <TableCell sx={{ py:0.5, px:1, textAlign:'center' }}>
                              <Typography sx={{ fontWeight:500, color:T.muted, fontSize:'0.7rem' }}>{index+1}</Typography>
                            </TableCell>
                            <TableCell sx={{ py:0.5, px:1 }}>
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:500, color:T.text }}>{ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}</Typography>
                            </TableCell>
                            <TableCell sx={{ py:0.5, px:1, cursor:clickable?'pointer':'default' }} onClick={() => clickable && handleLedgerDescClick(ledger)}>
                              <Typography sx={{ fontSize:'0.7rem', color:clickable?T.purple:T.text, fontWeight:clickable?600:400, '&:hover':clickable?{textDecoration:'underline'}:{} }}>
                                {ledger.description1 || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py:0.5, px:1, textAlign:'right' }}>
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:600, color:T.red }}>{debit > 0 ? `₹${debit.toLocaleString('en-IN',{minimumFractionDigits:2})}` : '-'}</Typography>
                            </TableCell>
                            <TableCell sx={{ py:0.5, px:1, textAlign:'right' }}>
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:600, color:T.green }}>{credit > 0 ? `₹${credit.toLocaleString('en-IN',{minimumFractionDigits:2})}` : '-'}</Typography>
                            </TableCell>
                            <TableCell sx={{ py:0.5, px:1, textAlign:'right', backgroundColor:(isTotal||isClose)?'transparent':(runBal>=0?'#f0fdf4':'#fef2f2') }}>
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:(isTotal||isClose)?T.muted:(runBal>=0?T.green:T.red) }}>
                                {(isTotal||isClose) ? '-' : `₹${Math.abs(runBal).toLocaleString('en-IN',{minimumFractionDigits:2})}${runBal<0?' (Dr)':' (Cr)'}`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                    {custLedger.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign:'center', py:4 }}>
                          <Typography sx={{ color:T.muted, fontSize:'0.75rem' }}>
                            {selectedLedgerCust ? 'No ledger entries found' : 'Select a customer to view ledger'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Closing Balance */}
              {custLedger.length > 0 && (() => {
                const closingEntry = custLedger.find(l => (l.description1||'').toLowerCase().includes('closing balance'));
                const debit=parseFloat(closingEntry?.debit)||0; const credit=parseFloat(closingEntry?.credit)||0;
                const bal = credit - debit;
                return (
                  <Box sx={{ mt:1.5, mx:2, mb:2, p:1.5, background:'linear-gradient(135deg,#f8fafc,#f1f5f9)', borderRadius:'10px', display:'flex', justifyContent:'flex-end', alignItems:'center', gap:1.5, border:`1px solid ${T.border}` }}>
                    <Typography sx={{ fontWeight:700, color:T.text, fontSize:'0.8rem' }}>Closing Balance:</Typography>
                    <Box sx={{ px:2, py:0.75, borderRadius:'8px', background: bal >= 0 ? T.green : T.red }}>
                      <Typography sx={{ fontWeight:700, color:'#fff', fontSize:'0.85rem' }}>
                        ₹{Math.abs(bal).toLocaleString('en-IN',{minimumFractionDigits:2})} {bal < 0 ? '(Dr)' : '(Cr)'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </div>
          </GCard>

          {/* Modals */}
          {openSaleModal && (
            <SaleModal open={openSaleModal} onClose={() => setOpenSaleModal(false)} modalAction="view"
              formData={saleFormData} setFormData={setSaleFormData} saleDetails={saleDetails} setSaleDetails={setSaleDetails}
              onSave={() => setOpenSaleModal(false)} customers={ledgerModalCustomers} items={ledgerItems} banks={ledgerBanks}
              loading={modalLoading} companyId={companyId} branchId={branchId} token={token} onPrintInvoice={() => {}}/>
          )}
          {openPurchaseModal && (
            <PurchaseModal open={openPurchaseModal} onClose={() => setOpenPurchaseModal(false)} modalAction="view"
              formData={purchaseFormData} setFormData={setPurchaseFormData} purchaseDetails={purchaseDetails} setPurchaseDetails={setPurchaseDetails}
              onSave={() => setOpenPurchaseModal(false)} suppliers={suppliers} items={ledgerItems}
              loading={modalLoading} companyId={companyId} token={token} setLoading={() => {}} setLoadingMessage={() => {}}/>
          )}
          {openPaymentModal && (
            <PaymentReceiptsModal open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} modalAction="view"
              formData={paymentFormData} setFormData={setPaymentFormData} paymentReceiptDetails={paymentReceiptDetails} setPaymentReceiptDetails={setPaymentReceiptDetails}
              onSave={() => setOpenPaymentModal(false)} masters={paymentMasters} banks={ledgerBanks} purchases={[]} sales={[]}
              paymentMethods={paymentMethods} gstRates={[]} loading={modalLoading} companyId={companyId} branchId={branchId} token={token}/>
          )}
          {openJournalModal && (
            <JournalEntryModal open={openJournalModal} onClose={() => setOpenJournalModal(false)} modalAction="view"
              formData={journalFormData} setFormData={setJournalFormData} journalEntryDetails={journalEntryDetails} setJournalEntryDetails={setJournalEntryDetails}
              onSave={() => setOpenJournalModal(false)} masters={journalMasters} gstRates={[]}
              loading={modalLoading} companyId={companyId} branchId={branchId} token={token}/>
          )}
          {modalLoading && (
            <Box sx={{ position:'fixed', inset:0, display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'rgba(0,0,0,0.6)', zIndex:10000, backdropFilter:'blur(8px)' }}>
              <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2.5, background:'rgba(255,255,255,0.95)', border:`1px solid ${T.border}`, borderRadius:'20px', px:5, py:4, minWidth:200, boxShadow:T.shadowLg }}>
                <CircularProgress size={40} sx={{ color:T.purple }}/>
                <Typography sx={{ color:T.text, fontWeight:600, fontSize:'0.9rem' }}>Loading details...</Typography>
              </Box>
            </Box>
          )}
        </div>
      )}

    </div>
  );
};

export default SalesManDashboard;
